import { driveLink, folderOf } from '@jerome2/shared';
import { Icon } from '../ui/Icon.tsx';

// Bestanden die Jerome in OneDrive zette (een aangepaste factuur, een getekend contract): per bestand een link om te
// downloaden, te bekijken en in OneDrive te openen. De server haalt het bestand via de connector uit OneDrive. Elke link opent in
// een nieuw tabblad, ook downloaden: mislukt het ophalen, dan staat de vaste zin met het kenmerk in dat tabblad (les 4.27) en
// blijft de app staan; in hetzelfde tabblad verving de foutpagina de app (tot en met 2.2.44). Sinds 2.2.56 staat er per map een knop
// Open map (OpenFolder), ook onder een antwoord in de chat waarbij Jerome bestanden opsloeg: die opent de map in OneDrive via
// GET /api/files/link met map=1. De Verkenner van Windows kan een webpagina niet openen; de map opent in OneDrive in de browser.

export interface FileLink {
  name: string;
  path: string;
}

/** Route in de app voor een bestand in OneDrive. */
export function fileUrl(path: string, kind: 'download' | 'view' | 'open' = 'download'): string {
  const q = `path=${encodeURIComponent(path)}`;
  if (kind === 'open') return `/api/files/open?${q}`;
  return kind === 'download' ? `/api/files?${q}&download=1` : `/api/files?${q}`;
}

/** De mappen van een lijst bestanden, in volgorde en zonder dubbele (hoofdletters tellen niet mee, zoals in OneDrive), elk met het
 * eerste bestand erin: de route leidt de map af uit de webUrl van dat bestand. */
export function foldersOf(files: readonly FileLink[]): Array<{ folder: string; via: FileLink }> {
  const out: Array<{ folder: string; via: FileLink }> = [];
  for (const f of files) {
    const folder = folderOf(f.path);
    if (!out.some((o) => o.folder.toLowerCase() === folder.toLowerCase())) out.push({ folder, via: f });
  }
  return out;
}

/** Per map een knop Open map, in een nieuw tabblad; bij meer dan één map staat de naam van de map erbij. */
export function OpenFolder(props: { files: readonly FileLink[]; className?: string }) {
  const folders = foldersOf(props.files);
  if (!folders.length) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${props.className ?? ''}`}>
      {folders.map(({ folder, via }) => {
        const name = folder ? folder.slice(folder.lastIndexOf('/') + 1) : 'OneDrive';
        return (
          <a
            key={folder || '/'}
            href={driveLink(via.path, true)}
            target="_blank"
            rel="noopener"
            data-testid="open-map"
            aria-label={`Map ${name} openen in OneDrive`}
            title={`In OneDrive: ${folder || 'de hoofdmap'}`}
            className="inline-flex items-center gap-1.5 max-w-full rounded-lg border border-line bg-surface px-2.5 py-1 pointer-coarse:min-h-11 text-[13px] font-medium text-accent hover:bg-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Icon name="folder" size={14} className="shrink-0" />
            <span className="truncate">{folders.length > 1 ? `Open map ${name}` : 'Open map'}</span>
          </a>
        );
      })}
    </div>
  );
}

export function FileLinks(props: { files: FileLink[]; title?: string }) {
  return (
    <div className="text-[13px]">
      <p className="font-medium text-ink-soft mb-1">{props.title ?? 'Bestanden in OneDrive'}</p>
      <OpenFolder files={props.files} className="mb-1.5" />
      <ul className="space-y-1">
        {props.files.map((a) => (
          <li key={a.path} className="flex items-center gap-2 flex-wrap min-w-0">
            <Icon name="file" size={13} className="text-muted shrink-0" />
            <a href={fileUrl(a.path, 'download')} target="_blank" rel="noopener" className="font-medium text-accent hover:underline truncate" title={`Download ${a.name}`}>
              {a.name}
            </a>
            <span className="inline-flex items-center gap-2 text-[12px]">
              <a href={fileUrl(a.path, 'download')} target="_blank" rel="noopener" className="text-accent hover:underline" title="Opslaan op je computer">
                Download
              </a>
              <a href={fileUrl(a.path, 'view')} target="_blank" rel="noopener" className="text-accent hover:underline" title="Bekijken in een nieuw tabblad">
                Bekijk
              </a>
              <a href={fileUrl(a.path, 'open')} target="_blank" rel="noopener" className="text-accent hover:underline" title="Openen in OneDrive">
                In OneDrive
              </a>
            </span>
            <span className="text-muted truncate w-full pl-5" title={a.path}>
              {a.path}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
